/**
 * The allocation matrix: items down, suppliers across (prototype `vCompare`'s `.wb`/`.wbt`).
 *
 * Built to scale — real enquiries run to hundreds of items across many suppliers — so the
 * item column and the summary column are sticky while the supplier columns scroll
 * horizontally, and the header sticks on vertical scroll.
 *
 * Every number shown is derived in `lib/compare.ts`; this file only renders. Rows are keyed by
 * `(item_code, enquiry_row_no)` — a customer who asked for the same item on two enquiry lines
 * gets two independent rows here (Task 10: `comparison.get_comparison` sends one row per
 * enquiry line, not per item code), so `item_code` alone is not a safe React key.
 */

import { OfferCell } from './OfferCell'
import { AllocationCell, ItemCell, type InHouseOffer } from './RowCells'
import { bestUnitPrice, isBestOffer, rowsForItem, type NormalisedItem } from '../../lib/compare'
import type { AllocationRow, Currency } from '../../lib/types'
import type { DisplayCurrency } from '../../lib/displayCurrency'

export function WorkbenchTable({
  items,
  visibleSuppliers,
  supplierName,
  allocations,
  baseCurrency,
  display,
  onToggleCell,
  onSetQty,
  onAutoCover,
  onPriceLine,
  onUseOurStock,
  inHouseFor,
}: {
  items: NormalisedItem[]
  /** `Supplier.code` values — see `ComparisonSupplier.supplier`. */
  visibleSuppliers: string[]
  /** Code → display name, for rendering only; every key/comparison stays on the code. */
  supplierName: Map<string, string>
  allocations: AllocationRow[]
  baseCurrency: Currency
  /** The currency the customer is quoted in; figures are shown in it beside the base ones. */
  display?: DisplayCurrency
  onToggleCell: (itemCode: string, enquiryRowNo: string, supplier: string) => void
  onSetQty: (itemCode: string, enquiryRowNo: string, supplier: string, qty: number) => void
  onAutoCover: (itemCode: string, enquiryRowNo: string) => void
  /**
   * Prices the row from our own stock directly — the same write the dialog's "Use our stock"
   * makes, without the dialog (Akhil, 2026-09-18: "like we did in the RFQ builder"). The badge
   * is the offer; clicking it takes it.
   */
  onUseOurStock?: (rowNo: string) => void
  /** Opens the price-this-line dialog — our stock, contracts, past supplier prices — for a row. */
  onPriceLine?: (enquiryRowNo: string) => void
  /**
   * Our own price for the row's unit and what is free on the shelf — shown as a tag under the
   * item (Akhil, 2026-09-15) so the in-house option is visible beside the offers without opening
   * the dialog. `null` price means the catalogue has none for this unit.
   */
  inHouseFor?: (enquiryRowNo: string) => InHouseOffer
}) {
  return (
    <div className="wb">
      <table className="wbt">
        <thead>
          <tr>
            <th className="sticky">Item</th>
            <th className="right">Qty</th>
            {visibleSuppliers.map((supplier) => (
              <th key={supplier} className="right">
                {supplierName.get(supplier) ?? supplier}
              </th>
            ))}
            <th className="sum right">Allocation</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={visibleSuppliers.length + 3} style={{ color: 'var(--muted)', padding: 22, textAlign: 'center' }}>
                No items match the filter.
              </td>
            </tr>
          ) : null}

          {items.map((item) => {
            const allocation = rowsForItem(allocations, item)
            // Ranked among the suppliers actually shown: hiding the cheapest promotes the
            // next one to "best" rather than leaving the row unhighlighted (prototype 863).
            const best = bestUnitPrice(item, visibleSuppliers)

            return (
              <tr key={`${item.item_code}::${item.enquiry_row_no}`}>
                <ItemCell
                  item={item}
                  supplierName={supplierName}
                  onPriceLine={onPriceLine}
                  onUseOurStock={onUseOurStock}
                  inHouseFor={inHouseFor}
                />

                <td className="right mono">
                  {item.qty}
                  {/* The unit every offer on this row is ranked per — "10" alone is not a
                      quantity when suppliers quote in drums and pieces. */}
                  <div style={{ color: 'var(--muted)', fontSize: 11 }}>{item.uom}</div>
                </td>

                {visibleSuppliers.map((supplier) => {
                  const offer = item.bySupplier.get(supplier)
                  return (
                    <OfferCell
                      key={supplier}
                      offer={offer}
                      requiredQty={item.qty}
                      allocatedQty={allocation.find((row) => row.supplier === supplier)?.allocated_qty ?? 0}
                      isBest={!!offer && isBestOffer(offer, best)}
                      baseCurrency={baseCurrency}
                      display={display}
                      onToggle={() => onToggleCell(item.item_code, item.enquiry_row_no, supplier)}
                      onSetQty={(qty) => onSetQty(item.item_code, item.enquiry_row_no, supplier, qty)}
                    />
                  )
                })}

                <AllocationCell
                  item={item}
                  allocation={allocation}
                  supplierName={supplierName}
                  baseCurrency={baseCurrency}
                  display={display}
                  onAutoCover={onAutoCover}
                />
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
